import {
	ErrorModal,
	useShiftNavigator,
	useUserContext,
} from "@dexteel/mesf-core";
import { Card, CardContent, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import { useMemo, useState } from "react";
import { ShiftInfo } from "../models/ShiftInfo";
import { getTrendingData } from "../repositories/repositories";

export const useTrendingData = ({
	start,
	end,
	onError,
}: {
	start: Date | undefined;
	end: Date | undefined;
	onError: (error: Error) => void;
}) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const assetId = Number(defaultAreaId);

	return useQuery<ShiftInfo[], Error>({
		queryKey: ["trending", start, end, assetId],
		queryFn: ({ signal }) => getTrendingData({ start, end, assetId }, signal),
		onError: onError,
		enabled: !!start && !!end && !!assetId,
	});
};

export const ShiftChart = ({
	start,
	end,
	grid = {
		top: 10,
		left: 10,
		right: 10,
		containLabel: true,
	},
	cardHeight = 110,
	disableOnClick = false,
}: {
	start?: Date;
	end?: Date;
	grid?: {
		top?: number;
		left?: number;
		right?: number;
		bottom?: number;
		containLabel?: boolean;
	};
	cardHeight?: number;
	disableOnClick?: boolean;
}) => {
	const [error, setError] = useState("");

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const { data: trendingData, isLoading } = useTrendingData({
		start: start ?? shiftInfo?.CurrentStart,
		end: end ?? shiftInfo?.CurrentEnd,
		onError: (e) => setError(e.message),
	});

	const handleDateClick = (params: any) => {
		if (params.componentType === "series") {
			const clickedDate = params.name || params.data?.name;
			if (clickedDate) {
				const clickedAsDate = new Date(clickedDate + "/2025");
				getShiftDataFromAPI(clickedAsDate, 0);
			}
		}
	};

	const processedData = useMemo(() => {
		if (!trendingData || trendingData.length === 0) {
			return {
				productionDates: [],
				rowData: [[], []],
			};
		}

		// Group by ProductionDate
		const groupedData = trendingData.reduce(
			(acc, shift) => {
				const dateStr = shift.ProductionDate?.toString() || "";
				if (!dateStr) return acc;

				const date = new Date(dateStr);
				const isoKey = date.toISOString().split("T")[0];

				if (!acc[isoKey]) {
					acc[isoKey] = {
						productionTimes: [],
						delayTimes: [],
					};
				}
				acc[isoKey].productionTimes.push(shift.ProductionTimePercent || 0);
				acc[isoKey].delayTimes.push(shift.DelayTimePercent || 0);
				return acc;
			},
			{} as Record<string, { productionTimes: number[]; delayTimes: number[] }>,
		);

		const sortedDates = Object.keys(groupedData).sort();

		// Calculate average percentages for each date
		const displayDates = sortedDates.map((isoDate) => {
			const date = new Date(isoDate);
			const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
			const day = date.getUTCDate().toString().padStart(2, "0");
			return `${month}/${day}`;
		});

		const productionTimeData = sortedDates.map((date) => {
			const times = groupedData[date].productionTimes;
			return times.reduce((sum, time) => sum + time, 0) / times.length;
		});

		const delayTimeData = sortedDates.map((date) => {
			const times = groupedData[date].delayTimes;
			return times.reduce((sum, time) => sum + time, 0) / times.length;
		});

		return {
			productionDates: displayDates,
			rowData: [productionTimeData, delayTimeData],
		};
	}, [trendingData]);

	// Calculate stacked bar chart data
	const chartData = useMemo(() => {
		const { rowData, productionDates } = processedData;

		if (rowData[0].length === 0) {
			return { series: [], productionDates: [] };
		}

		// Calculate total for each date
		const totalData: number[] = [];
		for (let i = 0; i < rowData[0].length; ++i) {
			let sum = 0;
			for (let j = 0; j < rowData.length; ++j) {
				sum += rowData[j][i];
			}
			totalData.push(sum);
		}

		// Create series for stacked bars
		const series = ["Production Time", "Delay Time"].map((name, sid) => {
			return {
				name,
				type: "bar",
				stack: "total",
				barWidth: "60%",
				itemStyle: {
					color: sid === 0 ? "#2196F3" : "#FFBEBB",
				},
				emphasis: {
					itemStyle: {
						cursor: "pointer",
					},
				},
				data: rowData[sid].map((d, did) =>
					totalData[did] <= 0 ? 0 : d / totalData[did],
				),
			};
		});

		return { series, productionDates };
	}, [processedData]);

	const option = {
		tooltip: {
			appendToBody: true,
			trigger: "axis",
			formatter: function (params: any) {
				let result = params[0].name + "<br/>";
				params.forEach((param: any) => {
					result +=
						param.marker +
						" " +
						param.seriesName +
						": " +
						(param.value * 100).toFixed(1) +
						"%<br/>";
				});
				return result;
			},
			textStyle: {
				fontSize: 11,
			},
		},
		yAxis: {
			type: "value",
			max: 1,
			axisLabel: {
				formatter: function (value: number) {
					return value * 100 + "%";
				},
				fontSize: 10,
			},
		},
		xAxis: {
			type: "category",
			data: chartData.productionDates,
			axisLabel: {
				fontSize: 10,
			},
		},
		series: chartData.series,
		grid: {
			...grid,
			width: "95%",
			height: cardHeight - 20,
		},
	};

	return (
		<>
			<Card
				sx={{
					width: "100%",
					height: cardHeight,
					minWidth: 0,
					overflow: "visible",
				}}
				elevation={1}
			>
				<CardContent
					sx={{
						padding: "2px !important",
						width: "100%",
						height: "100%",
						boxSizing: "border-box",
					}}
				>
					{isLoading ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "100%",
								width: "100%",
							}}
						>
							<CircularProgress size="small" />
						</div>
					) : (
						<ReactECharts
							option={option}
							onEvents={{
								click: disableOnClick ? () => {} : handleDateClick,
							}}
							notMerge
							lazyUpdate
							style={{
								cursor: "pointer",
								width: "100%",
							}}
						/>
					)}
				</CardContent>
			</Card>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
